import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";


import { estadosUF } from "./FormSimulationLists";
import { Estado, OptionItem, SimulationFormProps } from "./FormSimulation.types";
import { ButtonDefault, Form, FormSimulationContainer, RecaptchaWrapper } from "./FormSimulation.style";
import TextField from "../form/TextField/TextField";
import SelectField from "../form/SelectField/SelectField";
import CheckBox from "../form/CheckBox/CheckBox";
import { tagCtaClick } from "../../utils/tagManager";
import Modal from "../Modal/Modal";
import { getEcidCookie } from "../../utils/cookieUtils";

declare const window: any;

const ReCAPTCHA = React.lazy(() => import("react-google-recaptcha"));

const FormSimulation: React.FC<SimulationFormProps> = ({
  personalButton,
  nameLabel,
  namePlaceHolder,
  nameError,
  nameRequired,
  nameRequiredText,
  emailLabel,
  emailPlaceHolder,
  emailError,
  emailRequired,
  emailRequiredText,
  celularLabel,
  celularPlaceHolder,
  celularError,
  celularRequired,
  celularRequiredText,
  estadoLabel,
  estadoPlaceHolder,
  estadoRequired,
  estadoRequiredText,
  cidadeLabel,
  cidadePlaceHolder,
  cidadeRequired,
  cidadeRequiredText,
  checkBoxText,
  checkBoxRequired,
  checkBoxRequiredText,
  title,
  titleColor,
  subTitle,
  subTitleColor,
  backgroundColor,
  requestError,
  siteKey,
  children,
  isInEditor,
  simFormBtnFontColor,
  simFormBtnBgColor,
  backgroundInputColor,
  labelColor,
  placeholderColor,
  textInputColor,
  formLink,
  idCrm,
  statusBreve,
  isModalOpen: isModalOpenProp = false,
  modalButton,
  titleModal,
  showModalOnSuccess,
  modalAutoClose = false,
  modalAutoCloseDelay = 3000,
  isInModal,
  onClose,
  id
}) => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [estado, setEstado] = useState("");
  const [estadoUF, setEstadoUF] = useState("");
  const [cidade, setCidade] = useState("");
  const [isChecked, setChecked] = useState(false);
  const [isReCaptchaChecked, setReCaptchaChecked] = useState(false);
  const [list, setList] = useState<Estado[]>([]);
  const [listState, setListState] = useState<OptionItem[]>();
  const [listCity, setListCity] = useState<OptionItem[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [showRecaptcha, setShowRecaptcha] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [shouldFetchData, setShouldFetchData] = useState(false);
  
  const [isFullNameValid, setFullNameValid] = useState(false);
  const [isEmailValid, setEmailValid] = useState(false);
  const [isPhoneValid, setPhoneValid] = useState(false);
  // Estado do modal com debug
  const [isModalOpen, setModalOpen] = useState(isModalOpenProp);

  // Debug do estado do modal
  useEffect(() => {
    console.log("Modal state changed:", isModalOpen);
  }, [isModalOpen]);

  const formRef = useRef<HTMLDivElement>(null);
  const [hasFiredViewTag, setHasFiredViewTag] = useState(false);

  const pathMatch = window.location.pathname.match(/^\/imoveis\/([^/]+)\/([^/]+)\/([^/]+)/);
  
  useEffect(() => {
    if (estado && !estadoUF) {
      const codigoEstado = obterCodigoPorSigla(estado) || "";
      setEstadoUF(codigoEstado);
    }
  }, [estado, cidade]);

  const estadoMap: Record<string, { sigla: string; codigo: string }> =
    useMemo(() => {
      return estadosUF.reduce((acc, { estado, sigla, codigo }) => {
        acc[estado] = { sigla, codigo };
        return acc;
      }, {} as Record<string, { sigla: string; codigo: string }>);
    }, []);

  function obterCodigoPorSigla(sigla: string): string | null {
    for (const estado in estadoMap) {
      if (estadoMap[estado].sigla === sigla) {
        return estadoMap[estado].codigo;
      }
    }
    return "";
  }

  useEffect(() => {

    if (!formRef.current || shouldFetchData) return;
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.1) {
          setShouldFetchData(true);
          observer.disconnect();
        }
      });
    },
    { threshold: 0.1 } // 10% do componente visível
  );
  observer.observe(formRef.current);
  return () => observer.disconnect();
}, [shouldFetchData]);

useEffect(() => {
  if (dataFetched || !shouldFetchData) return; 

  const fetchData = async () => {
    try {
      setDataFetched(true); 

      const sessionKey = "state-data";
      const cachedData = sessionStorage.getItem(sessionKey);
      let result;

      if (cachedData) {
        result = JSON.parse(cachedData);
      } else {
      const url = "/bin/mrv/properties/state-cities/list";
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.statusText}`);
      }
      result = await response.json();
      sessionStorage.setItem(sessionKey, JSON.stringify(result));
    }

      let formattedData: any[] = [];

      if (result && result.states && Array.isArray(result.states)) {
        formattedData = result.states.map((item: any) => {
          const estadoNome = item.label || item.name || item.estado || "";      
          let cidadesList = [];
          
          if (item.cities) {
            cidadesList = item.cities;
          } else if (item.cidades) {
            cidadesList = item.cidades;
          } else if (item.municipios) {
            cidadesList = item.municipios;
          }
          
          const cidadesProcessadas = Array.isArray(cidadesList)
            ? cidadesList
                .map((c: any) => {
                  
                  if (typeof c === 'string') {
                    return c;
                  } else if (c && typeof c === 'object') {
                    return c.label || c.name || c.value || c.cidade || c.municipio || '';
                  }
                  return '';
                })
                .filter((city: string) => city && city.trim().length > 0)
                .sort((a: string, b: string) => a.localeCompare(b))
            : [];          
          return {
            estado: estadoNome,
            sigla: estadoMap[estadoNome]?.sigla || item.sigla || item.value || "",
            numero: estadoMap[estadoNome]?.codigo || "",
            cidades: cidadesProcessadas,
          };
        });

        const mgFinal = formattedData.find(item => item.sigla === 'MG');
      }
      if (formattedData && Array.isArray(formattedData)) {

        const sortedData = formattedData.sort((a: any, b: any) =>
          a.estado.localeCompare(b.estado)
        );

        setList(sortedData);

        const ListStates = sortedData.map((item: any) => ({
          value: item.sigla,
          label: item.estado,
        }));
        setListState(ListStates);

      } else {
        throw new Error("Dados processados inválidos");
      }
    } catch (error) {
      console.error("Erro ao buscar os estados e cidades:", error);
      setDataFetched(false); 
    }
  };

    fetchData();
   
  }, [shouldFetchData, estadoMap]);

  useEffect(() => {
    const stateData = list.find((item) => item.sigla === estado);
    if (stateData) {
      console.log("Estado encontrado no useEffect:", stateData); // Debug
    
    const cities = stateData.cidades
      .filter((city) => city && typeof city === 'string' && city.trim().length > 0)
      .map((city) => ({
        value: city.toUpperCase(),
        label: city
          .toLowerCase()
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
      }));
      setListCity(cities);
    }
  }, [list, estado]);

  // useEffect para exibir reCAPTCHA (com setTimeout)
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowRecaptcha(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);
 
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    setIsLoading(true);

    try {
      const responseToken = await fetch("/libs/granite/csrf/token.json");
      const jsonToken = await responseToken.json();
      const url = `/bin/mrv/falecomamia`;
      const storeData = {
          telefone: `55${phone}`,
          nome: fullName,
          estadoInteresse: estadoUF,
          cidadeInteresse: cidade,
          email: email,
          idCrm: idCrm,
          statusBreve: statusBreve
        }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "CSRF-Token": jsonToken.token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(storeData),
      });

      if (response.status === 200) {
        sessionStorage.setItem("isTalkToMiaFormCompleted", "true");

        tagCtaClick({
          element_text: personalButton || 'Enviar',
          element_location: isInModal ? 'modal' : 'body',
          content_id: id || 'form-simulation-submit',
          section_title: title || 'Formulário de Simulação'
        });

        window.dataLayer = window.dataLayer || [];
        
        const baseEventData = {
          event: "form_sent",
          form_name: title || "FormSimulation",
          email: email,
          phone: `+55${phone}`,
          ecid: getEcidCookie(),
        };

        if(isInModal){
          window.dataLayer.push({
            event: "form_sent",
            form_name: "Formulario Modal Mia",
            email: email,
            phone:`+55${phone}`,
            content_type: "whatsApp",
            ecid: getEcidCookie(),
          });
        }else if(window.location.pathname === "/minha-casa-minha-vida"){
          window.dataLayer.push({
            ...baseEventData,
            income_range: "R$ 3.500 a R$ 5.499",
            state: estado,
            city: cidade,
            content_type: "Form simulação - minha-casa-minha-vida"
          });
        } else if(window.location.pathname === "/financiamento"){
          window.dataLayer.push({
            ...baseEventData,
            income_range: "R$ 3.500 a R$ 5.499",
            state: estado,
            city: cidade,
            content_type: "Form simulação - financiamento"
          });
        } else if(window.location.pathname === "/whats"){
          window.dataLayer.push({
            ...baseEventData,
            content_type: "Form Simulação - whatsApp"
          });
        } else if(pathMatch && pathMatch[0] && window.location.pathname === pathMatch[0]){
          window.dataLayer.push({
            ...baseEventData,
            income_range: "R$ 3.500 a R$ 5.499",
            state: estado,
            city: cidade,
            content_type: "Form simulação - imoveis/ apartamentos"
          });
        }
          
        if(!isInModal){
            showModalOnSuccess && setModalOpen(true);
        }else{
          onClose();
        }
        
      } else {
        throw new Error(`Erro na requisição: ${response.status}`);
      }

      setIsLoading(false);
      if (!statusBreve) {
        window.open(formLink, '_blank');
      }
    } catch (error) {
      console.error("Erro ao enviar os dados:", error);   
      setIsLoading(false);
      if (requestError) { requestError(true); }
    }
      
  };

   useEffect(() => {
    if (!formRef.current || hasFiredViewTag) return;


    let page = "simulacao";
    if (window.location.pathname === "/minha-casa-minha-vida") {
      page = "minha casa minha vida";
    } else if (window.location.pathname === "/financiamento") {
      page = "financiamento";
    } else if (pathMatch && pathMatch[0] && window.location.pathname === pathMatch[0]) {
      page = "imoveis/ apartamentos";
    } else if (window.location.pathname === ("/whats")) {
      page = "whatsApp";
    } else if (isInModal){
      page = "Modal Mia"
    }

    const observer = new window.IntersectionObserver(
      (entries: { isIntersecting: any; intersectionRatio: number; }[]) => {
        entries.forEach((entry: { isIntersecting: any; intersectionRatio: number; }) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
            if (!hasFiredViewTag) {
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({
                event: "view_form",
                form_name: title || "FormSimulation",
                content_type: "Form Simulação - " + page,
                form_step: isInModal? "undefined" : "1/3",
                ecid: getEcidCookie(),
              });
              setHasFiredViewTag(true);
              observer.disconnect();
            }
          }
        });
      },
      { threshold: [0.25] }
    );

    observer.observe(formRef.current);

    return () => observer.disconnect();
  }, [title, hasFiredViewTag]);

  const handleChangeEstado = async (value: string) => {
    setEstado(value);
    const codigo = obterCodigoPorSigla(value) || "";
    setEstadoUF(codigo);
    const stateData = list.find((item) => item.sigla === value);
    if (stateData && stateData.cidades) {
      console.log("Cidades brutas:", stateData.cidades); // Debug
      
      const cities = stateData.cidades
        .filter((city) => city && typeof city === 'string' && city.trim().length > 0) // Filtrar cidades válidas
        .map((city) => ({
          value: city.toUpperCase(), // Manter valor em maiúsculo para consistência
        label: city
          .toLowerCase()
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
      }));
      setListCity(cities);
      if (cities.length > 0) {
        setCidade(cities[0].value);
        
      } else {
        setCidade("");
        console.log("Nenhuma cidade encontrada"); // Debug
      }
    }
  };

  const handleChangeCidade = (value: string) => {
    setCidade(value);
  };

  const closeModal = () => {
    console.log("Fechando modal...");
    setModalOpen(false);
  };

  return (
    <FormSimulationContainer ref={formRef} className="formSimulation" $backgroundColor={backgroundColor} $titleColor={titleColor} $subTitleColor={subTitleColor} id={id || "formulario"} $backgroundInputColor={backgroundInputColor} $labelColor={labelColor} $placeholderColor={placeholderColor} $textInputColor={textInputColor} isInModal={isInModal}>
      <h2 className="title-form" tabIndex={0}>{title}</h2>
      <p className="subtitle-form" tabIndex={0}>{subTitle}</p>
      {children}
      <Form onSubmit={handleSubmit}>
        <TextField
          id={`nome-completo-${id || "form"}`}
          label={nameLabel}
          type="text"
          placeholder={namePlaceHolder}
          isRequired={nameRequired}
          requiredLabel={nameRequiredText}
          value={fullName}
          onChange={(value: any) => setFullName(value)}
          onValidityChange={setFullNameValid}
          errorMessage={!isFullNameValid ? nameError : ""}
        />
        <div className="style-input">
          <TextField
            id={`phone-${id || "form"}`}
            label={celularLabel}
            type="tel"
            value={phone}
            placeholder={celularPlaceHolder}
            isRequired={celularRequired}
            requiredLabel={celularRequiredText}
            onChange={(value: any) => setPhone(value)}
            onValidityChange={setPhoneValid}
            errorMessage={celularError}
          />

          <TextField
            id={`email-${id || "form"}`}
            label={emailLabel}
            type="email"
            placeholder={emailPlaceHolder}
            isRequired={emailRequired}
            value={email}
            requiredLabel={emailRequiredText}
            onChange={(value: any) => setEmail(value)}
            onValidityChange={setEmailValid}
            errorMessage={emailError}
          />

        </div>
        
        <div className="style-input">
          <SelectField
            id={`estado-${id || "form"}`}
            label={estadoLabel}
            isRequired={estadoRequired}
            optionsList={listState}
            value={estado}
            placeholder={estadoPlaceHolder}
            requiredLabel={estadoRequiredText}
            onChange={(value: any) => handleChangeEstado(value)}
          />
          <SelectField
            id={`cidade-${id || "form"}`}
            label={cidadeLabel}
            isRequired={cidadeRequired}
            requiredLabel={cidadeRequiredText}
            disable={!estado}
            value={cidade}
            optionsList={listCity}
            placeholder={cidadePlaceHolder}
            onChange={(value: any) => handleChangeCidade(value)}
          />

        </div>
        <div className="style-check">
          <CheckBox
            id={`politica-${id || "form"}`}
            text={checkBoxText}
            isRequired={checkBoxRequired}
            requiredLabel={checkBoxRequiredText}
            onChange={(isChecked: boolean) => {
              setChecked(isChecked);
            }}
          />

        </div>
        
        <RecaptchaWrapper>
          {showRecaptcha ? (
            <Suspense fallback={<div style={{ color: 'black', padding: '10px', textAlign: 'center' }}>Carregando...</div>}>
              <ReCAPTCHA
                sitekey={siteKey ? siteKey : "MRV_SITE_KEY"}
                onChange={() => {
                  setReCaptchaChecked(true);
                }}
                onErrored={() => {
                  console.error("[FormSimulation] Erro no carregamento do reCAPTCHA!");
                }}
                asyncScriptOnLoad={() => {
                }}
              />
            </Suspense>
          ) : null}
        </RecaptchaWrapper>
        <ButtonDefault
          color={simFormBtnFontColor}
          backgroundColor={simFormBtnBgColor}
          type="submit"
          id="btn-simulacao"
          disabled={
            !isFullNameValid ||
            !isEmailValid ||
            !isPhoneValid ||
            !isChecked ||
            !cidade || !estado ||
            !isReCaptchaChecked ||
            isLoading
          }
        >
          {isLoading ? "Enviando..." : personalButton}
        </ButtonDefault>
      </Form>

      {isModalOpen && (titleModal || modalButton) && (
        <Modal onClose={closeModal} onReturn={closeModal} returnDisabled={true} hideArrow={true}>
          <h2 className="modal-title">{titleModal || "Sucesso!"}</h2>
          <ButtonDefault className="default" type="button" onClick={closeModal} aria-label={modalButton || "Fechar"}>
            {modalButton || "Fechar"}
          </ButtonDefault>
        </Modal>
      )}

    </FormSimulationContainer>
  );
}

export default FormSimulation;
